import {Card} from 'platform/components';
import {Box, HStack, Show, Text, type ThemeColorPath, VStack} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';
import {Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';

import {isNil, mapObjIndexed} from 'ramda';

import {ChartTooltipContent, useChartColors, useChartStyle} from '@dms/features/report-charts';
import i18n from '@dms/i18n';
import {getQuantityWithHandlingUnit} from '@dms/warehouse/shared';

import type {ArticleStockAgingBucket} from '@prompt-bridge/shared/validations';

import {EMPTY_PLACEHOLDER} from 'shared/web';

// The age buckets of the dead stock report end at the same ages (90, 180 and 365 days), so these
// buckets take its colours: blue for the freshest stock, red for stock older than a year. Stock
// older than two years has no bucket there and takes a darker red.
const BUCKET_APPEARANCE = {
  months0to3: {range: '0–3', color: 'palettes.blue.60.100'},
  months3to6: {range: '3–6', color: 'palettes.green.60.100'},
  months6to12: {range: '6–12', color: 'palettes.orange.60.100'},
  months12to24: {range: '12–24', color: 'palettes.red.60.100'},
  months24plus: {range: '24+', color: 'palettes.red.80.100'},
} as const satisfies Record<
  ArticleStockAgingBucket['bucket'],
  {range: string; color: ThemeColorPath}
>;

const BUCKET_COLORS = mapObjIndexed((appearance) => appearance.color, BUCKET_APPEARANCE);

// 32 px, the height of the breakdown bars in the warehouse report.
const BAR_HEIGHT = 8;
const BAR_MARGIN = {top: 0, right: 0, bottom: 0, left: 0};

interface StatisticsAgingProps {
  buckets: ArticleStockAgingBucket[];
  currency: string;
  handlingUnitLabel: string;
}

export function StatisticsAging(props: StatisticsAgingProps) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const chartStyle = useChartStyle();
  const colors = useChartColors(BUCKET_COLORS);
  const isComplete = props.buckets.every((bucket) => bucket.isComplete);
  const totalQuantity = props.buckets.reduce((sum, bucket) => sum + (bucket.quantity ?? 0), 0);
  const totalValue = props.buckets.reduce((sum, bucket) => sum + (bucket.purchaseValue ?? 0), 0);
  const title = i18n.t('entity.warehouse.reporting.valueByStockAge');
  const monthsLabel = i18n.t('general.labels.months');
  const labelOf = (bucket: ArticleStockAgingBucket) =>
    BUCKET_APPEARANCE[bucket.bucket].range + ' ' + monthsLabel;
  const formatValue = (value: number | null) =>
    isNil(value)
      ? EMPTY_PLACEHOLDER
      : (formatCurrency(value, props.currency, 2) ?? EMPTY_PLACEHOLDER);
  const formatDetail = (bucket: ArticleStockAgingBucket) => {
    const quantity = isNil(bucket.quantity)
      ? EMPTY_PLACEHOLDER
      : getQuantityWithHandlingUnit(bucket.quantity, props.handlingUnitLabel);
    const share = isNil(bucket.sharePercent)
      ? EMPTY_PLACEHOLDER
      : (formatNumber(bucket.sharePercent, 1) ?? EMPTY_PLACEHOLDER) + ' %';
    return `${quantity} · ${share}`;
  };

  // A bucket without value takes no part of the bar, so the first and the last part are the bar
  // ends, and only they are rounded.
  const barBuckets = props.buckets.filter((bucket) => (bucket.purchaseValue ?? 0) > 0);
  const chartRow = Object.fromEntries(
    barBuckets.map((bucket) => [bucket.bucket, bucket.purchaseValue ?? 0])
  );
  const radiusOf = (index: number): [number, number, number, number] => {
    const start = index === 0 ? chartStyle.barCornerRadius : 0;
    const end = index === barBuckets.length - 1 ? chartStyle.barCornerRadius : 0;
    return [start, end, end, start];
  };

  return (
    <Card title={title} parameters={[i18n.t('general.labels.today')]} isFullHeight>
      {isComplete ? (
        <VStack spacing={4}>
          <HStack justify="space-between" align="center" wrap>
            <Text color="secondary">
              {getQuantityWithHandlingUnit(totalQuantity, props.handlingUnitLabel)}
            </Text>
            <Text>{formatCurrency(totalValue, props.currency, 2)}</Text>
          </HStack>
          <Show when={barBuckets.length > 0}>
            <Box width="100%" height={BAR_HEIGHT}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={[chartRow]} margin={BAR_MARGIN}>
                  {/* The bar is the whole stock value, so it spans the full width. */}
                  <XAxis type="number" domain={[0, 'dataMax']} hide />
                  <YAxis type="category" hide />
                  <Tooltip
                    cursor={false}
                    formatter={(value: number, name: string) => [formatValue(value), name]}
                    labelFormatter={() => title}
                    content={<ChartTooltipContent />}
                  />
                  {barBuckets.map((bucket, index) => (
                    <Bar
                      key={bucket.bucket}
                      dataKey={bucket.bucket}
                      name={labelOf(bucket)}
                      stackId="stockAge"
                      fill={colors[bucket.bucket]}
                      radius={radiusOf(index)}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Show>
          <VStack spacing={2}>
            {props.buckets.map((bucket) => (
              <HStack key={bucket.bucket} spacing={3} align="center" justify="space-between">
                <HStack spacing={2} align="center">
                  <Box
                    width={3}
                    height={3}
                    borderRadius="small"
                    backgroundColor={BUCKET_APPEARANCE[bucket.bucket].color}
                    flexShrink={0}
                  />
                  <Text size="small">{labelOf(bucket)}</Text>
                </HStack>
                <VStack align="flex-end">
                  <Text size="small" alternative>
                    {formatValue(bucket.purchaseValue)}
                  </Text>
                  <Text size="xSmall" color="secondary">
                    {formatDetail(bucket)}
                  </Text>
                </VStack>
              </HStack>
            ))}
          </VStack>
        </VStack>
      ) : (
        <Text>{i18n.t('entity.warehouse.reporting.unknownDataNote')}</Text>
      )}
    </Card>
  );
}
